import type { Mark } from '@material-ui/core';
import { utils } from '@ps/poker-games-tile-mappers';
import type { Currency } from '@ps/web-core-enums';

import { formatDecimal } from '@swf/core/utils/formatting';
import { getUSNumberFormat } from '@swf/poker';
import type { PlayMode } from '@swf/poker';

const SLIDER_EXTENDED_LENGTH = {
  RM: 5,
  PM: 8
};

const SLIDER_VALUES_INITIAL = {
  RM: [
    0, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000,
    2500, 5000, 10000, 20000, 25000, 50000
  ],
  PM: [
    0, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000, 25000, 50000, 100000,
    200000, 250000, 500000, 1000000, 2000000, 2500000, 5000000, 10000000, 20000000, 25000000,
    50000000, 100000000, 200000000, 250000000, 500000000, 1000000000, 2000000000, 2500000000,
    5000000000, 10000000000, 20000000000, 25000000000, 50000000000
  ]
};

export const SLIDER = {
  RM: {
    VALUE_MAX: 50000,
    DEFAULT_MIN: 0,
    DEFAULT_MAX: SLIDER_VALUES_INITIAL.RM.length - 1 + SLIDER_EXTENDED_LENGTH.RM,
    DEFAULT_VALUE: [0, SLIDER_VALUES_INITIAL.RM.length - 1 + SLIDER_EXTENDED_LENGTH.RM],
    MINIMUM_DISTANCE: SLIDER_EXTENDED_LENGTH.RM + 1
  },
  PM: {
    VALUE_MAX: 50000000000,
    DEFAULT_MIN: 0,
    DEFAULT_MAX: SLIDER_VALUES_INITIAL.PM.length - 1 + SLIDER_EXTENDED_LENGTH.PM,
    DEFAULT_VALUE: [0, SLIDER_VALUES_INITIAL.PM.length - 1 + SLIDER_EXTENDED_LENGTH.PM],
    MINIMUM_DISTANCE: SLIDER_EXTENDED_LENGTH.PM + 1
  }
};

const SLIDER_VALUES = {
  RM: SLIDER_VALUES_INITIAL.RM.concat(
    new Array(SLIDER_EXTENDED_LENGTH.RM).fill(SLIDER.RM.VALUE_MAX)
  ),
  PM: SLIDER_VALUES_INITIAL.PM.concat(
    new Array(SLIDER_EXTENDED_LENGTH.PM).fill(SLIDER.PM.VALUE_MAX)
  )
};

export const RANGE = {
  RM: {
    ID_MAX: 'maxBuyIn',
    ID_MIN: 'minBuyIn',
    MAX_LENGTH: 6,
    INPUT_VALIDATION: /^(?!0[0-9]|[.,]|[0]\d*[,])\d*([.,]\d*)?$/,
    DECIMAL_THRESHOLD: 999,
    TO_FIXED_THRESHOLD: 10
  },
  PM: {
    ID_MAX: 'maxBuyIn',
    ID_MIN: 'minBuyIn',
    MAX_LENGTH: 14,
    INPUT_VALIDATION: /^(?!0[0-9]|[.,]|[0]\d*[,])\d*([.,]\d*)*?$/,
    DECIMAL_THRESHOLD: 999,
    TO_FIXED_THRESHOLD: 10
  }
};

const getSliderMarkValuesMap = (playMode: PlayMode) =>
  SLIDER_VALUES[playMode].reduce<Record<number, number>>((acc, value, index) => {
    acc[index] = value;
    return acc;
  }, {});

const getSliderMarksMap = (playMode: PlayMode) =>
  SLIDER_VALUES[playMode].map((_, index) => ({ value: index, label: '' }));

export const SLIDER_MARK_VALUE_MAP = {
  RM: getSliderMarkValuesMap('RM'),
  PM: getSliderMarkValuesMap('PM')
};

export const SLIDER_MARKS: Record<PlayMode, Mark[]> = {
  RM: getSliderMarksMap('RM'),
  PM: getSliderMarksMap('PM')
};

export const findClosestMarkIndex = (inputValue: number, playMode: PlayMode) => {
  let index = 0;
  let smallestDiff = inputValue;

  SLIDER_VALUES[playMode].forEach((value, i) => {
    const difference = Math.abs(inputValue - value);
    if (difference < smallestDiff) {
      smallestDiff = difference;
      index = i;
    }
  });

  return index;
};

export const findClosestMaxMarkIndex = (inputValue: number, playMode: PlayMode) =>
  findClosestMarkIndex(inputValue, playMode) + SLIDER_EXTENDED_LENGTH[playMode] ||
  SLIDER[playMode].MINIMUM_DISTANCE;

export const parseRangeInput = (value: string, playMode: PlayMode) => {
  if (!RANGE[playMode].INPUT_VALIDATION.test(value)) return undefined;

  const [wholeNumber] = value.replaceAll(',', '').split('.');
  const numValue = Number(wholeNumber);

  switch (true) {
    case numValue >= SLIDER[playMode].VALUE_MAX:
      return undefined;
    case numValue > RANGE[playMode].DECIMAL_THRESHOLD:
      return formatDecimal(wholeNumber);
    default:
      return formatDecimal(value.replace(',', '.'));
  }
};

export const formatRangeInput = (
  value: string | number | undefined,
  options: { shouldFormatToFixed?: boolean; playMode: PlayMode } = { playMode: 'RM' }
) => {
  if (!value) return null;

  const numValue = Number(value);

  if (numValue > RANGE[options.playMode].DECIMAL_THRESHOLD) {
    return getUSNumberFormat(numValue);
  }

  const shouldFormatToFixed =
    options?.shouldFormatToFixed &&
    numValue < RANGE[options.playMode].TO_FIXED_THRESHOLD &&
    numValue % 1 > 0;

  return shouldFormatToFixed ? `${numValue.toFixed(2)}` : value;
};

export const resolveSliderDistance = (
  oldValue: number[],
  newValue: number[],
  playMode: PlayMode,
  minimumDistance = SLIDER[playMode].MINIMUM_DISTANCE
) => {
  const activeThumbMin = oldValue[0] !== newValue[0];
  const [oldMin, oldMax] = oldValue;
  const [newMin, newMax] = newValue;
  let resolvedMin = SLIDER[playMode].DEFAULT_MIN;
  let resolvedMax = SLIDER[playMode].DEFAULT_MAX;

  if (activeThumbMin) {
    resolvedMin = Math.min(newMin, oldMax - minimumDistance);
    resolvedMax = oldMax;
  } else {
    resolvedMin = oldMin;
    resolvedMax = Math.max(newMax, oldMin + minimumDistance);
  }

  return [resolvedMin, resolvedMax];
};

export const resolveRangeInputValues = (min: number, max: number, playMode: PlayMode) => {
  const resolvedMax = max === SLIDER[playMode].DEFAULT_MIN ? NaN : max;
  let filterMax = resolvedMax;
  let filterMin = min > resolvedMax ? resolvedMax : min;
  let rangeMin = min === SLIDER[playMode].DEFAULT_MIN ? undefined : filterMin;
  let rangeMax: number | undefined = resolvedMax;
  let sliderMin = findClosestMarkIndex(min, playMode);
  let sliderMax = findClosestMaxMarkIndex(resolvedMax, playMode);

  if (isNaN(filterMin)) {
    filterMin = SLIDER[playMode].DEFAULT_MIN;
    rangeMin = undefined;
  }

  if (isNaN(filterMax)) {
    filterMax = SLIDER[playMode].VALUE_MAX;
    sliderMax = SLIDER[playMode].DEFAULT_MAX;
    rangeMax = undefined;
  }

  const sliderDiff = sliderMax - sliderMin;

  sliderMin =
    sliderDiff < SLIDER[playMode].MINIMUM_DISTANCE &&
    sliderMax - SLIDER[playMode].MINIMUM_DISTANCE >= 0
      ? sliderMax - SLIDER[playMode].MINIMUM_DISTANCE
      : sliderMin;

  return {
    filterValue: [filterMin, filterMax],
    sliderValue: [sliderMin, sliderMax],
    rangeValue: [rangeMin, rangeMax]
  };
};

export const resolveActiveFilterLabel = (
  filterValue: number[],
  defaultMin: string,
  defaultMax: string,
  playMode: PlayMode,
  isPlayMoney: boolean,
  currency?: Currency
) => {
  const [filterMin, filterMax] = filterValue;
  const max = filterMax < SLIDER[playMode].VALUE_MAX ? filterMax : undefined;
  const minLabel = filterMin
    ? utils.formatCurrency(filterMin * 100, currency, {
        separators: true,
        isPlayMoney,
        scalePM: 0
      })
    : defaultMin;
  const maxLabel = max
    ? utils.formatCurrency(max * 100, currency, {
        separators: true,
        isPlayMoney,
        scalePM: 0
      })
    : defaultMax;

  return `${minLabel} - ${maxLabel}`;
};

export const getMinRangeValueByMark = (mark: number, playMode: PlayMode) =>
  SLIDER_MARK_VALUE_MAP[playMode][mark];

export const getMaxRangeValueByMark = (mark: number, playMode: PlayMode) => {
  const value = SLIDER_MARK_VALUE_MAP[playMode][mark - SLIDER_EXTENDED_LENGTH[playMode]];
  return value >= SLIDER[playMode].VALUE_MAX ? undefined : value;
};

export const getSliderMarkValues = (min: number, max: number, playMode: PlayMode) => [
  SLIDER_MARK_VALUE_MAP[playMode][min],
  SLIDER_MARK_VALUE_MAP[playMode][max - SLIDER_EXTENDED_LENGTH[playMode]]
];
